import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import EnumRiskLevel from "@wallet-manager/node-types/dist/types/postgres/const/RiskLevel";

import { Dialog, DialogTitle } from "../MuiGenerals";
import { useAlerting } from "../../hooks";
import { RESCAN_LOCK_TIME_MS } from "../../utils/constant";
import { useZusDialogStore } from "../../zustand/store";

import ProviderResponseDialog from "./ProviderResponseDialog";
import RescanApply from "./RescanApply";
import RescanConfirm from "./RescanConfirm";
import RescanLocked from "./RescanLocked";

import {
  useApplyRescanRiskLevel,
  useGetRescanResult,
} from "./hooks/useRescanRiskLevel";
import { WalletRiskLevelRescanStatus } from "@wallet-manager/node-types/dist/src/postgres/const";

interface RescanRiskLevelDialogProps {
  refresh?: () => void;
}

// Named per the requirement doc (re-scan-requirement.md) so the type reads
// like the prose: locked → confirm → apply.
type DialogStep = "locked" | "confirm" | "apply";

const isWithinLock = (
  rescan_time_raw: unknown,
  lock_time_ms: number | undefined,
): boolean => {
  if (!rescan_time_raw) return false;
  const ts = new Date(rescan_time_raw as string).getTime();
  if (Number.isNaN(ts)) return false;
  // Prefer the API-provided lock duration, fall back to the compile-time
  // constant when the API hasn't responded yet (or for offline tests).
  const lockDuration = lock_time_ms ?? RESCAN_LOCK_TIME_MS;
  return Date.now() - ts < lockDuration;
};

const RescanRiskLevelDialog = ({ refresh }: RescanRiskLevelDialogProps) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`walletManagement.${key}`);
  const tEnum = (key: string) => translate(`enumConstants.${key}`);
  const zusDialog = useZusDialogStore();
  const { alerting } = useAlerting();
  const { getRescan } = useGetRescanResult();
  const { applyRescan } = useApplyRescanRiskLevel();

  // Initial step depends on whether the wallet is currently locked. Re-renders
  // within the locked step tick a `now` value inside RescanLocked, so the
  // user sees the Rescan button enable automatically when the lock expires
  // without us having to re-derive `step` here.
  //
  // Callers can override the starting step by passing `initialStep` in meta:
  //   * The Wallet Management tab passes nothing (or "confirm") — the user
  //     triggers a fresh rescan, so they walk through confirm → apply.
  //   * The Risk Checking Record tab passes "apply" — the rescan has
  //     already happened and the result is in the historical record, so
  //     skipping the re-query step lands directly on apply.
  const [step, setStep] = useState<DialogStep>(() => {
    const requested = zusDialog.meta?.initialStep as DialogStep | undefined;
    if (
      requested === "apply" ||
      requested === "locked" ||
      requested === "confirm"
    ) {
      // Even when the caller asks for "apply", the wallet may have just been
      // rescanned and may still be locked — honor the lock in that case.
      if (
        requested === "apply" &&
        isWithinLock(
          zusDialog.meta?.rescan_time_raw,
          zusDialog.meta?.lock_time_ms,
        )
      ) {
        return "locked";
      }
      return requested;
    }
    return isWithinLock(
      zusDialog.meta?.rescan_time_raw,
      zusDialog.meta?.lock_time_ms,
    )
      ? "locked"
      : "confirm";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOpen = zusDialog.match("rescanRiskLevelDialog");

  // Whenever the dialog re-opens (or meta changes for a different row),
  // re-derive the initial step from the current row's lock state and the
  // caller's `initialStep` hint. Subsequent setMeta calls within a flow
  // don't touch `rescan_time_raw` or `initialStep`, so this won't yank the
  // user out of the confirm / apply step mid-flow.
  useEffect(() => {
    if (!isOpen) return;
    const requested = zusDialog.meta?.initialStep as DialogStep | undefined;
    if (
      requested === "apply" ||
      requested === "locked" ||
      requested === "confirm"
    ) {
      if (
        requested === "apply" &&
        isWithinLock(
          zusDialog.meta?.rescan_time_raw,
          zusDialog.meta?.lock_time_ms,
        )
      ) {
        setStep("locked");
        return;
      }
      setStep(requested);
      return;
    }
    setStep(
      isWithinLock(
        zusDialog.meta?.rescan_time_raw,
        zusDialog.meta?.lock_time_ms,
      )
        ? "locked"
        : "confirm",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    zusDialog.meta?.rescan_time_raw,
    zusDialog.meta?.lock_time_ms,
    zusDialog.meta?.initialStep,
  ]);

  const handleClose = () => {
    if (isSubmitting) return;
    setStep("confirm");
    zusDialog.closeAll();
  };

  const handleProceedFromLocked = () => {
    // Rescan is enabled only after the lock expires; RescanLocked already
    // disables the button. Reaching this handler means the lock is over.
    setStep("confirm");
  };

  const handleConfirm = async () => {
    const { wallet_id, current_risk_level } = zusDialog.meta || {};

    if (wallet_id === undefined) {
      alerting("warning", "Missing wallet context");
      return;
    }

    setIsSubmitting(true);
    // Identify the wallet by its primary key. The backend resolves chain
    // membership from wallet_id alone.
    const rescanResult = await getRescan({
      wallet_id: Number(wallet_id),
    });
    setIsSubmitting(false);

    if (!rescanResult) {
      // Global error handler already toasted; remain in the confirm step.
      return;
    }

    const currentLabel = EnumRiskLevel[Number(current_risk_level)]
      ? tEnum(EnumRiskLevel[Number(current_risk_level)])
      : String(current_risk_level);

    // Spread the mapped result into meta. `rescan_id` is the new piece —
    // the dialog needs it later for the apply call.
    zusDialog.setMeta({
      ...zusDialog.meta,
      ...rescanResult,
      current_risk_level_label: currentLabel,
    });

    setStep("apply");
  };

  const handleApply = async () => {
    const { rescan_id } = zusDialog.meta || {};

    if (rescan_id === undefined) {
      alerting("warning", "Missing rescan context");
      return;
    }

    setIsSubmitting(true);
    const ok = await applyRescan({
      wallet_risk_level_rescan_id: rescan_id,
      action: 1,
    });
    setIsSubmitting(false);

    if (!ok) return;

    setStep("confirm");
    zusDialog.closeAll();
    refresh?.();
  };

  // Map of step → renderer. Adding a new step is a one-line change.
  const stepContent: Record<DialogStep, React.ReactNode> = {
    locked: <RescanLocked onProceed={handleProceedFromLocked} />,
    confirm: <RescanConfirm onConfirm={async () => await handleConfirm()} />,
    apply: <RescanApply onApply={async () => await handleApply()} />,
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t("rescan_risk_level")}</DialogTitle>
        {stepContent[step]}
      </Dialog>

      <ProviderResponseDialog />
    </>
  );
};

export default RescanRiskLevelDialog;
